import { useState } from "react";
import { Loader2, PackageSearch, RefreshCw, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import api from "../lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "never");
const LABEL = { printing: "being printed", shipped: "shipped", delivered: "delivered", cancelled: "cancelled" };

const OrderRow = ({ o }) => (
  <li data-testid={`shop-order-${o.order_name}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs py-1.5 border-b border-border/60 last:border-0">
    <span className="font-mono2 font-semibold">{o.order_name}</span>
    <Link to={`/s/${o.duck_id}`} className="underline truncate max-w-[12rem]" title={o.duck_id}>{o.duck_name || o.duck_id.slice(0, 8)}</Link>
    <span className="text-muted-foreground">{o.height ? `${o.height} mm` : ""}{o.quantity > 1 ? ` ×${o.quantity}` : ""}</span>
    <span className={`rounded-full px-2 py-0.5 ${o.status === "printing" ? "bg-accent/15 text-accent" : "bg-muted"}`}>{LABEL[o.status] || o.status}</span>
    <span className="text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</span>
    {o.tracking_url && (
      <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline">tracking <ExternalLink className="h-3 w-3" /></a>
    )}
  </li>
);

// Order polling status + the latest Duckify orders (admin only).
export const ShopOrdersPanel = ({ orders, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const poll = orders?.poll;

  const pollNow = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/library/shop/orders/poll");
      await onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Poll failed");
    }
    setBusy(false);
  };

  return (
    <div data-testid="shop-orders-panel" className="rounded-2xl border border-border bg-background/60 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground"><PackageSearch className="h-4 w-4" /> Duck orders</p>
        <Button data-testid="shop-orders-poll-btn" variant="outline" size="sm" className="rounded-full gap-2" onClick={pollNow} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Poll now
        </Button>
      </div>
      <p data-testid="shop-orders-status" className="text-xs text-muted-foreground">
        Polled every 10 min · last {when(poll?.at)}{poll?.at ? ` · ${poll.seen} orders seen, ${poll.matched} duck lines` : ""}
        {poll?.error && <span className="text-destructive"> · {poll.error}</span>}
      </p>
      {error && <p data-testid="shop-orders-error" className="text-sm text-destructive">{error}</p>}
      {orders?.recent?.length ? (
        <ul data-testid="shop-orders-list">{orders.recent.map((o) => <OrderRow key={`${o.order_name}-${o.duck_id}`} o={o} />)}</ul>
      ) : (
        <p data-testid="shop-orders-empty" className="text-xs text-muted-foreground">No Duckify orders yet. Makers see "Ordered · being printed" on their share page once one arrives.</p>
      )}
    </div>
  );
};
